
import { supabase } from "../integrations/supabase/client";

export interface FeedbackData {
  title: string;
  description: string;
  type: 'suggestion' | 'bug' | 'question' | 'other';
  anonymous_name?: string;
  anonymous_email?: string;
}

export async function submitFeedback(feedbackData: FeedbackData) {
  const { data: { user } } = await supabase.auth.getUser();
  
  // Handle both authenticated and anonymous users
  if (user?.id) {
    // Authenticated user
    const { error } = await supabase
      .from('feedback')
      .insert({
        user_id: user.id,
        title: feedbackData.title,
        description: feedbackData.description,
        feedback_type: feedbackData.type,
        status: 'pending'
      });

    if (error) {
      throw new Error(`Error submitting feedback: ${error.message}`);
    }
  } else {
    // Anonymous user - require name and email
    if (!feedbackData.anonymous_name || !feedbackData.anonymous_email) {
      throw new Error("For anonymous feedback, please provide your name and email");
    }

    const { error } = await supabase
      .from('feedback')
      .insert({
        title: feedbackData.title,
        description: feedbackData.description,
        feedback_type: feedbackData.type,
        status: 'pending',
        anonymous_name: feedbackData.anonymous_name,
        anonymous_email: feedbackData.anonymous_email
      });

    if (error) {
      throw new Error(`Error submitting feedback: ${error.message}`);
    }
  }

  // Notify admins of new feedback
  const userName = user?.user_metadata?.full_name || 
                  feedbackData.anonymous_name || 
                  'Anonymous User';
  
  await notifyAdminsOfNewFeedback({
    title: feedbackData.title,
    feedback_type: feedbackData.type,
    user_name: userName
  });

  return true;
}

// Admin function to get all feedback
export async function getAllFeedback() {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User must be authenticated');
  }

  const { data, error } = await supabase
    .from('feedback')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(`Error fetching feedback: ${error.message}`);
  }

  return data || [];
}

// Admin function to update feedback status
export async function updateFeedbackStatus(
  feedbackId: string, 
  status: 'in_progress' | 'resolved', 
  response?: string
) {
  try {
    const updateData: any = { status };
    if (response) {
      updateData.response = response;
    }

    const { error } = await supabase
      .from('feedback')
      .update(updateData)
      .eq('id', feedbackId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error updating feedback status:', error);
    return false;
  }
}

// Create notification for admins when feedback is submitted
export async function notifyAdminsOfNewFeedback(feedbackData: {
  title: string;
  feedback_type: string;
  user_name: string;
}) {
  try {
    // Get all admin users
    const { data: adminUsers, error: adminError } = await supabase
      .from('admin_users')
      .select('user_id')
      .in('role', ['admin', 'moderator']);

    if (adminError || !adminUsers?.length) {
      console.log('No admin users found or error fetching admins');
      return;
    }

    // Create notifications for all admins
    const notifications = adminUsers.map(admin => ({
      recipient_user_id: admin.user_id,
      type: 'feedback_submitted',
      content_preview: `New ${feedbackData.feedback_type}: ${feedbackData.title} from ${feedbackData.user_name}`,
      related_entity_type: 'feedback',
    }));

    const { error: notificationError } = await supabase
      .from('notifications')
      .insert(notifications);

    if (notificationError) {
      console.error('Error creating admin notifications:', notificationError);
    }
  } catch (error) {
    console.error('Error notifying admins:', error);
  }
}

export async function getUserFeedback() {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError || !user || !user.id) {
    throw new Error("User not authenticated");
  }

  const { data, error } = await supabase
    .from('feedback')
    .select('*')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false }) as any; // Using 'as any' temporarily until types are properly updated

  if (error) {
    throw new Error(`Error fetching feedback: ${error.message}`);
  }

  return data || [];
}
